/**
 * The News entry in the navigation: its icon and its "something new" dot.
 *
 * NOT WIRED INTO THE SHELL. Exported for Shell.tsx to place; see the hand-over
 * note for the exact lines.
 *
 * THE DOT
 * Shown when the signed-in person has releases they have not seen: the server
 * compares each release's publication time with `User.newsSeenAt`. Opening the
 * News page marks the news seen, and that page writes the cleared count straight
 * into this query's cache, so the dot disappears at once instead of waiting for
 * the next poll.
 *
 * The count is in the accessible name as well as the visual. A coloured dot says
 * nothing to a screen reader, and "3" next to a menu entry is ambiguous.
 *
 * COST
 * One small indexed request on first render, then at most one every five
 * minutes while the tab is visible, and again on window focus. It has its own
 * rate-limit allowance on the server.
 */

import type { SVGProps } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { useNewsT } from '../lib/newsStrings';

export interface NewsUnread {
  count: number;
  latestVersion: string | null;
}

/** Query key shared with the News page, which clears it on open. */
export const newsUnreadKey = (userId: string | undefined) => ['news', 'unread', userId] as const;

export function useNewsUnread() {
  const { session } = useAuth();
  const userId = session?.user.id;
  return useQuery({
    // Keyed by user: after signing out and in as someone else, the previous
    // person's "nothing new" must not be shown to the next.
    queryKey: newsUnreadKey(userId),
    queryFn: () => api.get<NewsUnread>('/news/unread'),
    enabled: !!userId,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    refetchIntervalInBackground: false,
    // A failed lookup simply shows no dot. A dot that cries wolf is worse.
    retry: false,
  });
}

/** Newspaper glyph, on the same 24px grid as components/icons.tsx. */
export const IconNews = (props: SVGProps<SVGSVGElement>) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    {...props}
  >
    <path d="M4 5a1 1 0 0 1 1-1h11a1 1 0 0 1 1 1v14a2 2 0 0 0 2 2H6a2 2 0 0 1-2-2V5Z" />
    <path d="M17 9h2a1 1 0 0 1 1 1v9a2 2 0 0 1-2 2" />
    <path d="M8 8h5M8 12h5M8 16h3" />
  </svg>
);

/**
 * `inline` sits at the end of a nav row (pushed right).
 * `corner` overlays the top-right of its positioned parent, for an icon-only
 * control such as the phone "More" button.
 */
export function NewsUnreadDot({ variant = 'inline', className }: {
  variant?: 'inline' | 'corner';
  className?: string;
}) {
  const { tx } = useNewsT();
  const { data } = useNewsUnread();
  const count = data?.count ?? 0;
  if (count <= 0) return null;

  return (
    <>
      <span
        aria-hidden
        className={cn(
          'shrink-0 rounded-full bg-brand ring-2 ring-surface',
          variant === 'inline' ? 'ml-auto w-2.5 h-2.5' : 'absolute -top-0.5 -right-0.5 w-2.5 h-2.5',
          className,
        )}
      />
      <span className="sr-only">{tx('news.navUnread', { count })}</span>
    </>
  );
}
